import {debounce, ROOT_DIALOG_IDENTIFIER_MAP} from "../misc/utils"
import {useRef} from "react"
import {buildRenditeChart} from "./altersvorsorge-luecke-schliesen-action"

const KEINE_LEBENSERWARTUNG = -1

const generateLabel = (jahr, alter) => [String(jahr), `(${alter})`]

const darkenHex = (hex, factor = 0.7) => {
    if (!hex) return hex
    const r = Math.round(parseInt(hex.slice(1, 3), 16) * factor)
    const g = Math.round(parseInt(hex.slice(3, 5), 16) * factor)
    const b = Math.round(parseInt(hex.slice(5, 7), 16) * factor)
    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`
}

const colorArray = (verlaufData, color, alterKey, highlightAlter) =>
    verlaufData?.map((d) =>
        +d[alterKey] === +highlightAlter ? darkenHex(color) : color,
    ) ?? []

const generateDatasets = (
    verlaufData,
    theme,
    highlightAlter,
    alterKey,
    target,
) => [
    {
        label: "Nettoeinkommen",
        data:
            verlaufData?.map(
                (dataset) => dataset.nettoVersorgungAusEinkuenften,
            ) ?? [],
        backgroundColor: colorArray(
            verlaufData,
            theme.VERSORGUNGSLUECKENVERLAUF?.einkuenfte,
            alterKey,
            highlightAlter,
        ),
    },
    {
        label: "Altersrente",
        data:
            verlaufData?.map(
                (dataset) => dataset.gesetzlicheVersorgungNettoEuro,
            ) ?? [],
        backgroundColor: colorArray(
            verlaufData,
            theme.VERSORGUNGSLUECKENVERLAUF?.rente,
            alterKey,
            highlightAlter,
        ),
    },
    {
        label: "neue Versorgung",
        data: verlaufData?.map((dataset) => dataset.neueVersorgungNetto) ?? [],
        backgroundColor: colorArray(
            verlaufData,
            theme.VERSORGUNGSLUECKENVERLAUF?.neueVersorgung,
            alterKey,
            highlightAlter,
        ),
    },
    {
        label: "Bestehende Versorgung",
        data:
            verlaufData?.map(
                (dataset) =>
                    dataset.summeNettorentenPrivateUndBetrieblicheBestehendeVersorgungEuro,
            ) ?? [],
        backgroundColor: colorArray(
            verlaufData,
            theme.VERSORGUNGSLUECKENVERLAUF?.bestehendeVersorgung,
            alterKey,
            highlightAlter,
        ),
    },
    {
        label: "Versorgungslücke Person 1",
        data:
            verlaufData?.map((dataset) =>
                target === "kunde" ? dataset.luecke : dataset.lueckePartner,
            ) ?? [],
        backgroundColor: colorArray(
            verlaufData,
            theme.VERSORGUNGSLUECKENVERLAUF?.luecke,
            alterKey,
            highlightAlter,
        ),
    },
    {
        label: "Versorgungslücke Ehepartner",
        data:
            verlaufData?.map((dataset) =>
                target === "kunde" ? dataset.lueckePartner : dataset.luecke,
            ) ?? [],
        backgroundColor: colorArray(
            verlaufData,
            theme.VERSORGUNGSLUECKENVERLAUF?.lueckePartner,
            alterKey,
            highlightAlter,
        ),
    },
]

const generateDatasetsVLAktuell = (verlaufData, theme) => [
    {
        label: "Nettoeinkommen",
        data: [verlaufData?.[5]?.nettoVersorgungAusEinkuenften],
        backgroundColor: theme.VERSORGUNGSLUECKENVERLAUF?.einkuenfte,
    },
    {
        label: "Altersrente",
        data: [verlaufData?.[5]?.gesetzlicheVersorgungNettoEuro],
        backgroundColor: theme.VERSORGUNGSLUECKENVERLAUF?.rente,
    },
    {
        label: "Bestehende Versorgung",
        data: [
            verlaufData?.[5]
                ?.summeNettorentenPrivateUndBetrieblicheBestehendeVersorgungEuro,
        ],
        backgroundColor: theme.VERSORGUNGSLUECKENVERLAUF?.bestehendeVersorgung,
    },
    {
        label: "Versorgungslücke",
        data: [verlaufData?.[5]?.luecke],
        backgroundColor: theme.VERSORGUNGSLUECKENVERLAUF?.luecke,
    },
    {
        label: "Versorgungslücke Ehepartner",
        data: [verlaufData?.[5]?.lueckePartner],
        backgroundColor: theme.VERSORGUNGSLUECKENVERLAUF?.lueckePartner,
    },
]

const generateDatasetsVLNeu = (verlaufData, theme, neueVersorgung) => [
    {
        label: "Nettoeinkommen",
        data: [neueVersorgung?.nettoVersorgungAusEinkuenften],
        backgroundColor: theme.VERSORGUNGSLUECKENVERLAUF?.einkuenfte,
    },
    {
        label: "Altersrente",
        data: [neueVersorgung?.gesetzlicheVersorgungNettoEuro],
        backgroundColor: theme.VERSORGUNGSLUECKENVERLAUF?.rente,
    },
    {
        label: "Bestehende Versorgung",
        data: [
            neueVersorgung?.summeNettorentenPrivateUndBetrieblicheBestehendeVersorgungEuro,
        ],
        backgroundColor: theme.VERSORGUNGSLUECKENVERLAUF?.bestehendeVersorgung,
    },
    {
        label: "neue Versorgung",
        data: [neueVersorgung?.neueVersorgungNetto],
        backgroundColor: theme.VERSORGUNGSLUECKENVERLAUF?.neueVersorgung,
    },
    {
        label: "Versorgungslücke",
        data: [neueVersorgung?.luecke],
        backgroundColor: theme.VERSORGUNGSLUECKENVERLAUF?.luecke,
    },
    {
        label: "Versorgungslücke Ehepartner",
        data: [neueVersorgung?.lueckePartner],
        backgroundColor: theme.VERSORGUNGSLUECKENVERLAUF?.lueckePartner,
    },
]

const filterArtToKey = {
    ALTER: "alter",
    ZEITRAUM: "jahr",
}

export const filterVerlaufData = (verlaufData, von, bis, filterArt) => {
    const key = filterArtToKey[filterArt]
    if (!key || von == null || bis == null) return verlaufData ?? []
    return verlaufData?.filter((el) => el?.[key] >= von && el?.[key] <= bis)
}

const transformFoerdervergleich = (data, target) => {
    if (!data?.[target]) {
        return {}
    }
    const transformedVertaege = data[target]?.vertraege?.reduce(
        (acc, item) => ({
            ...acc,
            [item?.durchfuehrungsweg]: item,
        }),
        {},
    )
    return {[target]: {...data[target], vertraege: transformedVertaege}}
}

export const VersorgungslueckenVerlaufActions = ({
    sendDataToBackend,
    getDataFromPointer,
    updateData,
    theme,
}) => {
    const targetToPointerAktuell = {
        kunde: "/lueckeschliesen/chartAktuell",
        ehepartner: "/lueckeschliesen/chartAktuellPartner",
    }

    const targetToPointerNeu = {
        kunde: "/lueckeschliesen/chartNeu",
        ehepartner: "/lueckeschliesen/chartNeuPartner",
    }

    const buildAndSaveVerlauf = (
        verlaufData,
        lebenserwartung,
        target,
        basePointer,
    ) => {
        const alterKey = target === "kunde" ? "alter" : "alterPartner"
        const chart = {
            labels:
                verlaufData?.map((dataset) =>
                    generateLabel(dataset.jahr, dataset.alter),
                ) ?? [],
            datasets: generateDatasets(
                verlaufData,
                theme,
                lebenserwartung,
                alterKey,
                target,
            ),
            highlightIndex:
                verlaufData?.findIndex((d) => +d.alter === +lebenserwartung) ??
                -1,
        }
        updateData(chart, `/${basePointer}/${target}`)
    }

    const buildVerlauf = (
        verlaufData,
        lebenserwartung,
        target,
        ergebnisNeueVersorgung,
    ) => {
        buildAndSaveVerlauf(
            verlaufData,
            lebenserwartung,
            target,
            "ergebnisVersorgungslueckenVerlauf",
        )
        const chartZweiteSeiteAktuell = {
            bottomLabels: [""],
            datasets: generateDatasetsVLAktuell(verlaufData, theme),
        }
        const chartZweiteSeiteNeu = {
            bottomLabels: [""],
            datasets: generateDatasetsVLNeu(
                verlaufData,
                theme,
                ergebnisNeueVersorgung,
            ),
        }
        updateData(chartZweiteSeiteNeu, targetToPointerNeu[target])
        updateData(chartZweiteSeiteAktuell, targetToPointerAktuell[target])
    }

    const buildDetailChart = (verlauf, target) => {
        const verlaufData = verlauf?.[target] ?? []
        const chartData = {
            labels:
                verlaufData?.map((dataset) =>
                    generateLabel(dataset.jahr, dataset.alter),
                ) ?? [],
            datasets: [
                {
                    label: "gesetzliche Versorgung Brutto",
                    data: verlaufData?.map(
                        (item) => item?.gesetzlicheVersorgungBrutto,
                    ),
                    backgroundColor: "#003442",
                    stack: "brutto",
                },
                {
                    label: "gesetzliche Versorgung Netto",
                    data: verlaufData?.map(
                        (item) => item?.gesetzlicheVersorgungNettoEuro,
                    ),
                    backgroundColor: "#009119",
                    stack: "netto",
                },
            ],
        }
        updateData(chartData, `/avDetailansicht/${target}/chart`)
    }

    const buildAndFilterVerlaufForTarget = (
        verlaufData,
        verlaufGesamt,
        ergebnisAlter,
        ergebnisNeueVersorgung,
        target,
    ) => {
        const lebenserwartung =
            ergebnisAlter?.[target]?.lebenserwartungVersicherungsmathematisch
        const von = getDataFromPointer(`/appstate/zeitraum/von`)
        const bis = getDataFromPointer(`/appstate/zeitraum/bis`)
        const filterArt = getDataFromPointer(`/appstate/zeitraumArt`)
        const targetVerlauf = filterVerlaufData(
            verlaufData?.[target],
            von,
            bis,
            filterArt,
        )
        const targetVerlaufGesamt = filterVerlaufData(
            verlaufGesamt?.[target],
            von,
            bis,
            filterArt,
        )
        const neueVersorgung = ergebnisNeueVersorgung?.[target]
        buildVerlauf(targetVerlauf, lebenserwartung, target, neueVersorgung)
        buildAndSaveVerlauf(
            targetVerlaufGesamt,
            lebenserwartung,
            target,
            "ergebnisVersorgungslueckenVerlaufGesamt",
        )
    }

    const doCalculationAltersvorsorge = (updateResponse) => {
        const applyResult = (data) => {
            const {
                ergebnisVersorgungslueckenVerlauf: ergebnisVerlaufData,
                ergebnisAlter: ergebnisAlterData,
                ergebnisGesamtVerlauf,
                FoerdervergleichResult,
                ergebnisNeueVersorgung,
                berechnungsvorgabenFoerdervergleich,
                ergebnisAlterVorgezogenerBeginn,
                ergebnisStartenOderWarten,
            } = data ?? {}

            const foerdervergleich = {
                ...transformFoerdervergleich(FoerdervergleichResult, "kunde"),
                ...transformFoerdervergleich(FoerdervergleichResult, "partner"),
            }

            if (!ergebnisVerlaufData) {
                console.warn(
                    "[CALC_CONTEXTAREA_ALTERSVORSORGE] kein 'ergebnisVersorgungslueckenVerlauf' in Calc-Response",
                    Object.keys(data ?? {}),
                )
                return
            }

            buildDetailChart(ergebnisAlterVorgezogenerBeginn, "kunde")
            buildDetailChart(ergebnisAlterVorgezogenerBeginn, "ehepartner")
            buildRenditeChart(
                foerdervergleich,
                berechnungsvorgabenFoerdervergleich,
                updateData,
                "kunde",
            )
            buildRenditeChart(
                foerdervergleich,
                berechnungsvorgabenFoerdervergleich,
                updateData,
                "ehepartner",
            )
            buildAndFilterVerlaufForTarget(
                ergebnisVerlaufData,
                ergebnisGesamtVerlauf,
                ergebnisAlterData,
                ergebnisNeueVersorgung,
                "kunde",
            )
            buildAndFilterVerlaufForTarget(
                ergebnisVerlaufData,
                ergebnisGesamtVerlauf,
                ergebnisAlterData,
                ergebnisNeueVersorgung,
                "ehepartner",
            )
            buildAndSaveVerlauf(
                ergebnisStartenOderWarten?.kunde,
                KEINE_LEBENSERWARTUNG,
                "kunde",
                "ergebnisVersorgungslueckenVerlaufStartenOderWarten",
            )
            buildAndSaveVerlauf(
                ergebnisStartenOderWarten?.ehepartner,
                KEINE_LEBENSERWARTUNG,
                "ehepartner",
                "ergebnisVersorgungslueckenVerlaufStartenOderWarten",
            )

            updateData(
                ergebnisAlterVorgezogenerBeginn,
                "/ergebnisAlterVorgezogenerBeginn",
            )
            updateData(ergebnisStartenOderWarten, "/ergebnisStartenOderWarten")
            updateData(foerdervergleich, "/ergebnisFoerdervergleich")
            updateData(ergebnisAlterData ?? {}, "/ergebnisAlter")
            updateData(ergebnisNeueVersorgung ?? {}, "/ergebnisNeueVersorgung")
            updateData(ergebnisVerlaufData ?? {}, "/ergebnisVerlaufData")
            updateData(ergebnisGesamtVerlauf ?? {}, "/ergebnisGesamtVerlauf")
        }

        if (updateResponse?.calcData)
            return applyResult(updateResponse.calcData)

        const calcLink = updateResponse?._links?.calculate
        if (!calcLink?.href) {
            console.warn(
                "[CALC_CONTEXTAREA_ALTERSVORSORGE] 'calculate'-Link fehlt in Update-Response",
                Object.keys(updateResponse?._links ?? {}),
            )
            return
        }
        return sendDataToBackend(calcLink).then((result) =>
            applyResult(result?._data ?? {}),
        )
    }

    const latestCalcRef = useRef()
    latestCalcRef.current = doCalculationAltersvorsorge

    const debouncedSendDataRef = useRef()
    if (!debouncedSendDataRef.current) {
        debouncedSendDataRef.current = debounce(
            (...args) => latestCalcRef.current(...args),
            500,
        )
    }

    return {
        CALCULATE_VV: debouncedSendDataRef.current,
        CALC_CONTEXTAREA_ALTERSVORSORGE: doCalculationAltersvorsorge,
        LUECKE_OPEN_RENDITE: () => {
            updateData(true, "/appstate/renditeModalOpen")
        },
        LUECKE_CLOSE_RENDITE: () => {
            updateData(false, "/appstate/renditeModalOpen")
        },
        REBUILD_VERLAUF: () => {
            const verlauf = getDataFromPointer("/ergebnisVerlaufData")
            const verlaufGesamt = getDataFromPointer("/ergebnisGesamtVerlauf")
            const ergebnisNeueVersorgung = getDataFromPointer(
                "/ergebnisNeueVersorgung",
            )
            const ergebnisAlter = getDataFromPointer("/ergebnisAlter")
            buildAndFilterVerlaufForTarget(
                verlauf,
                verlaufGesamt,
                ergebnisAlter,
                ergebnisNeueVersorgung,
                "kunde",
            )
            buildAndFilterVerlaufForTarget(
                verlauf,
                verlaufGesamt,
                ergebnisAlter,
                ergebnisNeueVersorgung,
                "ehepartner",
            )
        },
        OPEN_STARTEN_ODER_WARTEN: () => {
            updateData(
                ROOT_DIALOG_IDENTIFIER_MAP.STARTEN_ODER_WARTEN,
                "/appstate/activeRootDialogIdentifier",
            )
        },
        CLOSE_STARTEN_ODER_WARTEN: () => {
            updateData(null, "/appstate/activeRootDialogIdentifier")
        },
        OPEN_AV_DETAILANSICHT: () => {
            updateData(
                ROOT_DIALOG_IDENTIFIER_MAP.AV_DETAILS,
                "/appstate/activeRootDialogIdentifier",
            )
        },
        CLOSE_AV_DETAILANSICHT: () => {
            updateData(null, "/appstate/activeRootDialogIdentifier")
        },
    }
}
